import React, { useCallback, useMemo } from 'react'
import styles from './socialMatchCard.module.scss'
import { Player, SocialMatchUpUI } from 'types/global'
import classNames from 'classnames'
import BtnSecondary from 'components/generic/BtnSecondary'
import BtnPrimary from 'components/generic/BtnPrimary'
import i18n from 'constants/strings'
import { Info } from 'components/Icons'
import { MatchUpSubmissionStateEnum } from 'types/api/enums'
import { useModalDispatch } from 'hooks/use-modal'
import { ProfileModalsState } from './panels/MyScoresPanel'
import { ActionPendingScoreModalType } from './modals/ActionPendingScoreModal'
import { doesUserNeedToSee } from 'utils/local-storage'
import { useDispatch, useSelector } from 'react-redux'
import { AppState } from 'redux/reducers'
import { approvePending, rejectPending } from 'actions/social-matches-actions'
import { ProfileModalId } from 'constants/modals'
import MatchCardMenu from './SocialMatchCardMenu'
import MatchCard, { MatchCardProps } from '../generic/MatchCard'

const InfoText = ({ text }: { text: string }) => (
  <p className={styles.matchCardInfoText}>
    <Info aria-hidden />
    <small>{text}</small>
  </p>
)

const FooterRejected = ({
  profileViewedId,
  profileViewedSide,
  rejectedById,
}: {
  profileViewedId: string
  profileViewedSide: Player[]
  rejectedById: string
}) => {
  const rejectingPlayer = profileViewedSide.find(p => p.id === rejectedById)
  let text = i18n.t('strSocialMatches.myScores.matchCard.rejected.info.opponent')

  if (rejectingPlayer) {
    rejectingPlayer.id === profileViewedId
      ? (text = i18n.t('strSocialMatches.myScores.matchCard.rejected.info.me'))
      : (text = i18n.t('strSocialMatches.myScores.matchCard.rejected.info.partner'))
  }

  return (
    <div className={classNames(styles.matchCardFooter, styles.matchCardRejected)}>
      <InfoText text={text} />
    </div>
  )
}

const FooterPending = ({
  matchId,
  isSubmittedByOpponent,
  disableActions = false,
}: {
  matchId: string
  isSubmittedByOpponent: boolean
  disableActions?: boolean
}) => {
  const ownTennisId = useSelector((state: AppState) => state.user.tennisId)
  const dispatch = useDispatch()
  const { modalDispatch: actionModalDispatch, modalActions: actionModalActions } = useModalDispatch<
    ProfileModalsState,
    ProfileModalId.ACTION_PENDING_SCORE
  >(ProfileModalId.ACTION_PENDING_SCORE)

  const { modalDispatch: infoModalDispatch, modalActions: infoModalActions } = useModalDispatch<
    ProfileModalsState,
    ProfileModalId.INFO_PENDING_SCORE
  >(ProfileModalId.INFO_PENDING_SCORE)

  const createClickHandler = useCallback(
    (modalType: ActionPendingScoreModalType) => () => {
      if (doesUserNeedToSee(modalType, ownTennisId)) {
        actionModalDispatch(
          actionModalActions.setValue({
            match: null,
            matchId: matchId,
            type: modalType,
          })
        )
        actionModalDispatch(actionModalActions.openModal())
      } else {
        dispatch(
          modalType === ActionPendingScoreModalType.APPROVE
            ? approvePending(matchId)
            : rejectPending(matchId)
        )
      }
    },
    [dispatch, matchId, actionModalActions, actionModalDispatch, ownTennisId]
  )

  const handleApprove = useMemo(
    () => createClickHandler(ActionPendingScoreModalType.APPROVE),
    [createClickHandler]
  )
  const handleReject = useMemo(
    () => createClickHandler(ActionPendingScoreModalType.REJECT),
    [createClickHandler]
  )

  const handleOpenInfo = useCallback(
    () => infoModalDispatch(infoModalActions.openModal()),
    [infoModalDispatch, infoModalActions]
  )

  return (
    <div className={classNames(styles.matchCardFooter, styles.matchCardPending)}>
      {isSubmittedByOpponent ? (
        <>
          <BtnSecondary
            size='small'
            className={styles.btnReject}
            onClick={handleReject}
            isDisabled={disableActions}
          >
            {i18n.t('strCommonUI.button.reject')}
          </BtnSecondary>
          <BtnPrimary size='small' onClick={handleApprove} isDisabled={disableActions}>
            {i18n.t('strCommonUI.button.accept')}
          </BtnPrimary>
        </>
      ) : (
        <button onClick={handleOpenInfo} className={styles.infoTextBtn}>
          <InfoText text={i18n.t('strSocialMatches.myScores.matchCard.pending.info')} />
        </button>
      )}
    </div>
  )
}

const CardFooter = ({
  match,
  profileViewedId,
  disableActions = false,
}: {
  match: SocialMatchUpUI
  profileViewedId: string
  disableActions?: boolean
}) => {
  const {
    id,
    sides: { SIDE1, SIDE2 },
    submittedStatus,
    submittedById,
    reviewedById,
  } = match
  const thisPlayerSide = [SIDE1, SIDE2].find(side => side.map(p => p.id).includes(profileViewedId))

  if (thisPlayerSide) {
    if (submittedStatus === MatchUpSubmissionStateEnum.REJECTED)
      return (
        <FooterRejected
          profileViewedId={profileViewedId}
          profileViewedSide={thisPlayerSide}
          rejectedById={reviewedById}
        />
      )
    if (submittedStatus === MatchUpSubmissionStateEnum.PENDING) {
      return (
        <FooterPending
          matchId={id}
          isSubmittedByOpponent={!thisPlayerSide.find(p => p.id === submittedById)}
          disableActions={disableActions}
        />
      )
    }
  }

  return null
}

interface Props extends MatchCardProps {
  match: SocialMatchUpUI
  isUpdating?: boolean
}

const SocialMatchCard = React.memo(
  ({ match, profileViewedId, isUpdating, truncateName = false }: Props) => {
    const { id, submittedStatus, submittedById } = match

    return (
      <MatchCard
        match={match}
        profileViewedId={profileViewedId}
        iconType={'avatar'}
        headerRight={
          (submittedStatus === MatchUpSubmissionStateEnum.PENDING ||
            submittedStatus === MatchUpSubmissionStateEnum.REJECTED) &&
          submittedById === profileViewedId && (
            <MatchCardMenu
              id={`matchCardMenu:${id}`}
              match={match}
              allowDelete
              isDisabled={isUpdating}
            />
          )
        }
        footer={
          profileViewedId ? (
            <CardFooter
              match={match}
              profileViewedId={profileViewedId}
              disableActions={isUpdating}
            />
          ) : null
        }
        truncateName={truncateName}
        className={classNames(styles.socialMatchCard, {
          [styles.cardUpdating]: isUpdating,
        })}
      />
    )
  }
)

export default SocialMatchCard
